import React from 'react';
import { Card, Avatar, Skeleton, Button, Flex } from 'antd';
import PropTypes from 'prop-types';
import { EditOutlined } from '@ant-design/icons';
import { bothListContent } from './listContent/bothListContent';
import { ListDocuments } from '../../../../../components/ListDocuments/ListDocuments';
import { outOfNavRoutes } from '../../../../../utils/constants/ownerPensionerRoutes';
import { CardTitle } from '../../../../../components/CardTitle/CardTitle';
import { InfoGrid } from '../../../../../components/InfoGrid/InfoGrid';

/**
 * React component for displaying general information about a user profile.
 * @component
 * @param {Object} props - The component properties.
 * @param {string} props.url - The URL of the profile photo.
 * @param {Object} props.profile - The profile information.
 * @param {string} props.profile.first_name - The first name of the user.
 * @param {string} props.profile.last_name - The last name of the user.
 * @param {string} [props.url=''] - The URL of the profile photo.
 * @param {Function} props.t - The translation function.
 * @param {Object} props.user - The user information.
 * @param {boolean} props.isLoading - Indicates whether the profile is in a loading state.
 * @param {Function} props.navigate - Function to navigate to a different page.
 * @param {string} props.profileID - The unique identifier of the user profile.
 * @param {Function} props.onPreview - Function to handle document preview.
 * @param {Array} props.personalDocuments - Array of personal documents associated with the profile.
 * @param {Function} props.downloadDocument - Function to handle document download.
 * @returns {JSX.Element} - React component.
 * @example
 * // Example usage of the GeneralInfos component:
 * <GeneralInfos
 *   url="/path/to/photo.jpg"
 *   profile={{
 *     first_name: "John",
 *     last_name: "Doe",
 *     _id: "123456789"
 *   }}
 *   t={translateFunction}
 *   user={{/* user information /*}}
 *   isLoading={false}
 *   navigate={navigateFunction}
 *   profileID="123456789"
 *   onPreview={handlePreview}
 *   personalDocuments={[/* array of personal documents /*]}
 *   downloadDocument={handleDownload}
 * />
 */
export const GeneralInfos = ({
  token,
  profile,
  photoId,
  t,
  user,
  isLoading,
  navigate,
  profileID,
  onPreview,
  personalDocuments,
  downloadDocument,
  checkProfileLock,
  profileLockMessage
}) => (
  <Card
    title={
      <CardTitle icon="user">
        {`${profile?.first_name || ''} ${profile?.last_name || ''}`}
      </CardTitle>
    }
    extra={
      <Flex gap={8} wrap>
        {profile?._id &&
          !profile?.secondary_profile?.first_name &&
          user?.role === 'users:PENSIONER' && (
            <Button
              onClick={() => {
                navigate(
                  `${outOfNavRoutes.PROFILE}/edit/${profileID}?fieldsActiveKey=secondaryProfileFields`
                );
              }}
            >
              {t('buttons.create_secondary_profile')}
            </Button>
          )}
        <Button
          icon={<EditOutlined />}
          onClick={() => {
            if (checkProfileLock) {
              profileLockMessage(t, 'profile');
              return;
            }
            navigate(
              `${outOfNavRoutes.PROFILE}/edit/${profileID}?fieldsActiveKey=${
                profile._id ? 'mainFields' : 'secondaryProfileFields'
              }`
            );
          }}
        >
          {t('buttons.edit')}
        </Button>
      </Flex>
    }
  >
    <Skeleton loading={isLoading} active avatar>
      <Flex gap={32} wrap align="flex-start">
        <Avatar
          size={96}
          src={
            photoId
              ? `${process.env.REACT_APP_API_URL}/pensioners/get-url-file/${photoId}/${token}`
              : undefined
          }
          style={{
            flexShrink: 0,
            background: 'var(--ds-cambridge-tint)',
            color: 'var(--ds-cambridge-dark)',
            fontSize: 30,
            fontWeight: 700
          }}
        >
          {`${profile?.first_name?.[0] || ''}${profile?.last_name?.[0] || ''}`}
        </Avatar>
        <div style={{ flex: 1, minWidth: 260 }}>
          <InfoGrid items={bothListContent({ profile, t, user })} />
        </div>
      </Flex>
      {user.role === 'users:PENSIONER' && (
        <div style={{ marginTop: 24 }}>
          <ListDocuments
            onPreview={onPreview}
            isLoading={isLoading}
            documents={personalDocuments}
            t={t}
            downloadDocument={downloadDocument}
            withTitle
          />
        </div>
      )}
    </Skeleton>
  </Card>
);

GeneralInfos.propTypes = {
  photoId: PropTypes.string,
  profile: PropTypes.shape({
    first_name: PropTypes.string,
    last_name: PropTypes.string,
    _id: PropTypes.string,
    secondary_profile: PropTypes.shape({
      first_name: PropTypes.string
    })
  }).isRequired,
  t: PropTypes.func.isRequired,
  user: PropTypes.shape({
    role: PropTypes.string
  }).isRequired,
  isLoading: PropTypes.bool.isRequired,
  navigate: PropTypes.func.isRequired,
  profileID: PropTypes.string.isRequired,
  onPreview: PropTypes.func.isRequired,
  personalDocuments: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  downloadDocument: PropTypes.func.isRequired,
  checkProfileLock: PropTypes.bool.isRequired,
  profileLockMessage: PropTypes.func.isRequired,
  token: PropTypes.string.isRequired
};

GeneralInfos.defaultProps = {
  photoId: ''
};
